import React, { useState, useEffect, useCallback } from 'react'
import { 
  Truck, 
  MapPin, 
  Clock, 
  CheckCircle, 
  Play, 
  Plus,
  Search,
  Users,
  Edit,
  Trash2,
  AlertTriangle,
  Bell,
  Route
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../config/api'
import MissionCreator from './MissionCreator'
import TaskManager from './TaskManager'

// Mission Dashboard - Content component for ResponsiveMacWindow
// This is a pure content component (no modal wrapper)

// Types based on our database schema
interface TransportMission {
  id: string
  mission_name: string
  mission_type: 'DELIVERY' | 'PICKUP' | 'ROUND_TRIP' | 'MULTI_STOP'
  mission_status: 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'DELAYED' | 'CANCELLED'
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  assigned_vehicle_id?: number
  assigned_driver?: string
  customer_name?: string
  completion_percentage: number
  current_stage?: string
  planned_start_time?: Date
  planned_end_time?: Date
  actual_start_time?: Date
  created_at: Date
}

// No more sample data - using real data from API

interface MissionDashboardProps {
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

// Helper functions to extract mission resources
const getMissionStaff = (mission: any) => {
  console.log('🔍 Extracting staff for mission:', mission.id, mission.mission_name)
  
  // If mission has staff_assignments array, use it
  if (mission.staff_assignments && Array.isArray(mission.staff_assignments)) {
    console.log('✅ Found staff_assignments:', mission.staff_assignments)
    return mission.staff_assignments
  }
  
  // Otherwise, create staff array from assigned_staff_id
  if (mission.assigned_staff_id && mission.assigned_driver) {
    const staffMember = {
      id: mission.assigned_staff_id,
      full_name: mission.assigned_driver,
      staff_type: 'DRIVER', // Default type
      role: 'PRIMARY'
    }
    console.log('✅ Created staff from assigned_staff_id:', staffMember)
    return [staffMember]
  }
  
  console.log('⚠️ No staff found for mission')
  return []
}

const getMissionVehicles = (mission: any) => {
  console.log('🔍 Extracting vehicles for mission:', mission.id, mission.mission_name)
  
  // If mission has vehicle_assignments array, use it
  if (mission.vehicle_assignments && Array.isArray(mission.vehicle_assignments)) {
    console.log('✅ Found vehicle_assignments:', mission.vehicle_assignments)
    return mission.vehicle_assignments
  }
  
  // Otherwise, create vehicle array from assigned_vehicle_id
  if (mission.assigned_vehicle_id) {
    const vehicle = {
      id: mission.assigned_vehicle_id,
      custom_name: mission.vehicle_name || `Vehicle ${mission.assigned_vehicle_id}`,
      custom_plate_number: mission.license_plate || 'No Plate',
      vehicle_name: mission.vehicle_name,
      license_plate: mission.license_plate
    }
    console.log('✅ Created vehicle from assigned_vehicle_id:', vehicle)
    return [vehicle]
  }
  
  console.log('⚠️ No vehicles found for mission')
  return []
}

const getMissionAlerts = (mission: any) => {
  console.log('🔍 Extracting alerts for mission:', mission.id, mission.mission_name)
  
  // If mission has alert_assignments array, use it
  if (mission.alert_assignments && Array.isArray(mission.alert_assignments)) {
    console.log('✅ Found alert_assignments:', mission.alert_assignments)
    return mission.alert_assignments
  }
  
  // For now, return empty array as alerts are not yet implemented in missions
  console.log('⚠️ No alerts found for mission (not yet implemented)')
  return []
}

const MissionDashboard: React.FC<MissionDashboardProps> = ({ showMessage }) => {
  const { t } = useTranslation()
  const [missions, setMissions] = useState<TransportMission[]>([])
  const [selectedMission, setSelectedMission] = useState<TransportMission | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [showMissionCreator, setShowMissionCreator] = useState(false)
  const [showTaskManager, setShowTaskManager] = useState(false)
  const [editingMission, setEditingMission] = useState<TransportMission | null>(null)
  const [deletingMission, setDeletingMission] = useState<TransportMission | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [statusBusy, setStatusBusy] = useState(false)
  const [missionAlerts, setMissionAlerts] = useState<any[]>([])
  const [alertsLoading, setAlertsLoading] = useState(false)
  const [pendingMissionId, setPendingMissionId] = useState<string | null>(null)
  const [statistics, setStatistics] = useState({
    total_missions: 0,
    active_missions: 0,
    completed_today: 0,
    delayed_missions: 0
  })

  const loadMissionAlerts = useCallback(async (missionId: string) => {
    setAlertsLoading(true)
    try {
      const response = await apiClient.get('/alerts', { mission_id: missionId, limit: 50 })
      setMissionAlerts(Array.isArray(response.data?.alerts) ? response.data.alerts : [])
    } catch {
      setMissionAlerts([])
    } finally {
      setAlertsLoading(false)
    }
  }, [])

  // Load missions from API
  useEffect(() => {
    loadMissions()
  }, [])

  useEffect(() => {
    const focusMission = (event: Event) => {
      const missionId = (event as CustomEvent<{ missionId?: string }>).detail?.missionId
      if (missionId) setPendingMissionId(String(missionId))
    }
    window.addEventListener('fleet:mission-focus', focusMission)
    return () => window.removeEventListener('fleet:mission-focus', focusMission)
  }, [])

  useEffect(() => {
    if (!pendingMissionId) return
    const match = missions.find(mission => String(mission.id) === pendingMissionId)
    if (!match) return
    setSelectedMission(match)
    setSearchTerm('')
    setFilterStatus('all')
    setPendingMissionId(null)
  }, [pendingMissionId, missions])

  useEffect(() => {
    if (selectedMission?.id) loadMissionAlerts(selectedMission.id)
    else setMissionAlerts([])
  }, [selectedMission?.id, loadMissionAlerts])

  const loadMissions = async () => {
    try {
      setLoading(true)
      console.log('🎯 Loading missions from API...')
      
      const response = await apiClient.get('/missions/dashboard')
      console.log('✅ Missions loaded:', response)
      
      // Handle different response structures
      const responseData = response.data || response
      const missionsArray = responseData.missions || responseData || []
      const statsData = responseData.statistics || {}
      
      if (Array.isArray(missionsArray) && missionsArray.length > 0) {
        // Transform API data to match component interface
        const transformedMissions = missionsArray.map(mission => {
          console.log('🔍 Raw mission from API:', {
            id: mission.id,
            name: mission.mission_name,
            assigned_staff_id: mission.assigned_staff_id,
            customer_id: mission.customer_id,
            customer_name: mission.customer_name,
            assigned_driver: mission.assigned_driver
          })
          
          return {
            ...mission,
            planned_start_time: mission.planned_start_time ? new Date(mission.planned_start_time) : undefined,
            planned_end_time: mission.planned_end_time ? new Date(mission.planned_end_time) : undefined,
            actual_start_time: mission.actual_start_time ? new Date(mission.actual_start_time) : undefined,
            created_at: new Date(mission.created_at || Date.now())
          }
        })
        
        setMissions(transformedMissions)
        setStatistics({
          total_missions: transformedMissions.length,
          active_missions: transformedMissions.filter(m => m.mission_status === 'IN_PROGRESS').length,
          completed_today: transformedMissions.filter(m => m.mission_status === 'COMPLETED').length,
          delayed_missions: transformedMissions.filter(m => m.mission_status === 'DELAYED').length,
          ...statsData
        })
        showMessage?.(`Loaded ${transformedMissions.length} missions with real staff`, 'success')
        console.log('✅ Missions loaded:', transformedMissions)
      } else {
        // No missions in database
        console.log('📝 No missions found in database')
        setMissions([])
        setStatistics({
          total_missions: 0,
          active_missions: 0,
          completed_today: 0,
          delayed_missions: 0
        })
        showMessage?.('No missions found', 'info')
      }
    } catch (error) {
      console.error('❌ Failed to load missions:', error)
      setMissions([])
      showMessage?.('Failed to load missions from server', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleMissionCreated = (response: any) => {
    // Refresh missions list after creating/updating mission
    loadMissions()
    
    // Handle different response structures
    const mission = response?.mission || response?.data || response
    const missionName = mission?.mission_name || mission?.name || 'New Mission'
    const action = editingMission ? 'updated' : 'created'
    
    showMessage?.(`Mission "${missionName}" ${action} successfully`, 'success')
    setEditingMission(null)
  }

  const handleDeleteMission = async () => {
    if (!deletingMission) return
    
    try {
      console.log(`🗑️ Deleting mission: ${deletingMission.id}`)
      await apiClient.delete(`/missions/${deletingMission.id}`)
      
      showMessage?.(`Mission "${deletingMission.mission_name}" deleted successfully!`, 'success')
      loadMissions() // Reload missions
      setShowDeleteConfirm(false)
      setDeletingMission(null)
      
      // Close mission details if deleted mission was selected
      if (selectedMission?.id === deletingMission.id) {
        setSelectedMission(null)
      }
      
    } catch (error) {
      console.error('❌ Failed to delete mission:', error)
      showMessage?.('Failed to delete mission', 'error')
    }
  }

  const transitionMission = async (action: 'start' | 'complete' | 'cancel') => {
    if (!selectedMission) return
    setStatusBusy(true)
    try {
      const response = await apiClient.post(`/missions/${selectedMission.id}/${action}`, {})
      const updated = response.data?.mission
      showMessage?.(
        action === 'start' ? 'Mission started' : action === 'complete' ? 'Mission completed' : 'Mission cancelled',
        'success'
      )
      await loadMissions()
      if (updated) {
        setSelectedMission(prev => prev && prev.id === updated.id
          ? {
              ...prev,
              mission_status: updated.mission_status,
              completion_percentage: updated.completion_percentage ?? prev.completion_percentage,
              actual_start_time: updated.actual_start_time ? new Date(updated.actual_start_time) : prev.actual_start_time
            }
          : prev)
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || `Failed to ${action} mission`
      showMessage?.(msg, 'error')
    } finally {
      setStatusBusy(false)
    }
  }

  // Filter missions
  const filteredMissions = missions.filter(mission => {
    const matchesStatus = filterStatus === 'all' || mission.mission_status === filterStatus
    const matchesSearch = mission.mission_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mission.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mission.assigned_driver?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesStatus && matchesSearch
  })

  // Utility functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'text-green-600 bg-green-100'
      case 'IN_PROGRESS': return 'text-blue-600 bg-blue-100'
      case 'DELAYED': return 'text-red-600 bg-red-100'
      case 'PLANNED': return 'text-gray-600 bg-gray-100'
      case 'CANCELLED': return 'text-gray-500 bg-gray-200'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'text-red-700 bg-red-200'
      case 'HIGH': return 'text-orange-700 bg-orange-200'
      case 'MEDIUM': return 'text-yellow-700 bg-yellow-200'
      case 'LOW': return 'text-green-700 bg-green-200'
      default: return 'text-gray-700 bg-gray-200'
    }
  }

  const getMissionTypeIcon = (type: string) => {
    switch (type) {
      case 'DELIVERY': return <Truck className="w-4 h-4" />
      case 'PICKUP': return <MapPin className="w-4 h-4" />
      case 'ROUND_TRIP': return <Route className="w-4 h-4" />
      case 'MULTI_STOP': return <Users className="w-4 h-4" />
      default: return <Truck className="w-4 h-4" />
    }
  }

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading_missions')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* Header with Stats */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-4 sm:space-x-6">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{statistics.active_missions}</div>
              <div className="text-xs text-gray-500">{t('active_missions')}</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-green-600">{statistics.completed_today}</div>
              <div className="text-xs text-gray-500">{t('completed_missions')}</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-red-600">{statistics.delayed_missions}</div>
              <div className="text-xs text-gray-500">{t('delayed_missions')}</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button 
              onClick={() => setShowMissionCreator(true)}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>{t('new_mission')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left Panel - Mission List */}
        <div className="w-full lg:w-1/3 bg-white border-r border-gray-200 flex flex-col min-h-0">
          {/* Filters and Search */}
          <div className="p-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3">
              <div className="relative flex-1 min-w-0">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('search_missions')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-1 sm:gap-2">
              {['all', 'IN_PROGRESS', 'PLANNED', 'DELAYED', 'COMPLETED'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-2 py-1 text-xs rounded-full transition-colors flex-shrink-0 ${
                    filterStatus === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status === 'all' ? 'All' : status.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Mission Cards */}
          <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
            {filteredMissions.map(mission => (
              <div
                key={mission.id}
                onClick={() => setSelectedMission(mission)}
                className={`group p-3 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                  selectedMission?.id === mission.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {/* Mission Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    {getMissionTypeIcon(mission.mission_type)}
                    <h3 className="font-semibold text-gray-900 truncate">{mission.mission_name}</h3>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(mission.priority)}`}>
                      {mission.priority}
                    </span>
                    {/* Mission Actions - Show on hover */}
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          console.log('🔧 Editing mission:', mission)
                          console.log('📝 Mission data for editing:', {
                            id: mission.id,
                            name: mission.mission_name,
                            assigned_staff_id: (mission as any).assigned_staff_id,
                            customer_id: (mission as any).customer_id,
                            customer_name: (mission as any).customer_name,
                            assigned_driver: (mission as any).assigned_driver
                          })
                          setEditingMission(mission)
                          setShowMissionCreator(true)
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title={t('edit_mission')}
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeletingMission(mission)
                          setShowDeleteConfirm(true)
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title={t('delete_mission')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Status and Progress */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(mission.mission_status)}`}>
                      {mission.mission_status.replace('_', ' ')}
                    </span>
                    <span className="text-sm font-medium text-gray-600">{mission.completion_percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${mission.completion_percentage}%` }}
                    />
                  </div>
                </div>

                {/* Mission Details */}
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex items-center space-x-2">
                    <Users className="w-3 h-3" />
                    <span>{mission.assigned_driver || t('unassigned')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{mission.customer_name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-3 h-3" />
                    <span>{mission.planned_start_time?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>

                {/* Current Stage */}
                {mission.current_stage && (
                  <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    {mission.current_stage}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Mission Details */}
        <div className="flex-1 bg-gray-50 overflow-hidden min-h-0">
          {selectedMission ? (
            <div className="h-full flex flex-col">
              {/* Mission Header */}
              <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      {getMissionTypeIcon(selectedMission.mission_type)}
                      <h2 className="text-2xl font-bold text-gray-900">{selectedMission.mission_name}</h2>
                      <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(selectedMission.mission_status)}`}>
                        {selectedMission.mission_status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-gray-600">{selectedMission.current_stage}</p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => setShowTaskManager(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>{t('manage_tasks')}</span>
                    </button>
                    {['PLANNED', 'DELAYED'].includes(selectedMission.mission_status) && (
                      <button
                        onClick={() => transitionMission('start')}
                        disabled={statusBusy}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50"
                      >
                        <Play className="w-4 h-4" />
                        <span>{t('start')}</span>
                      </button>
                    )}
                    {['IN_PROGRESS', 'DELAYED', 'PLANNED'].includes(selectedMission.mission_status) && (
                      <button
                        onClick={() => transitionMission('complete')}
                        disabled={statusBusy}
                        className="px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 flex items-center space-x-2 disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>{t('complete', { defaultValue: 'Complete' })}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{t('mission_progress')}</span>
                    <span className="text-sm font-medium text-gray-900">{selectedMission.completion_percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${selectedMission.completion_percentage}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Mission Content */}
              <div className="flex-1 p-4 overflow-y-auto">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
                  {/* Mission Details */}
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('mission_details')}</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Type</label>
                        <p className="text-gray-900">{selectedMission.mission_type.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Priority</label>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${getPriorityColor(selectedMission.priority)}`}>
                          {selectedMission.priority}
                        </span>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Driver</label>
                        <p className="text-gray-900">{selectedMission.assigned_driver || t('unassigned')}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">{t('customer')}</label>
                        <p className="text-gray-900">{selectedMission.customer_name}</p>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">{t('planned_start')}</label>
                        <p className="text-gray-900">
                          {selectedMission.planned_start_time?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">{t('planned_end')}</label>
                        <p className="text-gray-900">
                          {selectedMission.planned_end_time?.toLocaleString()}
                        </p>
                      </div>
                      {selectedMission.actual_start_time && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">{t('actual_start')}</label>
                          <p className="text-gray-900">
                            {selectedMission.actual_start_time.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Mission-linked alerts (real fleet_alerts with mission_id) */}
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    {t('mission_alerts', { defaultValue: 'Mission alerts' })}
                  </h3>
                  {alertsLoading ? (
                    <p className="text-sm text-gray-400">Loading…</p>
                  ) : missionAlerts.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {t('mission_alerts_empty', {
                        defaultValue: 'No alerts raised for this mission yet. Zone watches use the mission’s vehicles and geofences/corridors.'
                      })}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {missionAlerts.map(alert => (
                        <div key={alert.id} className="flex items-start gap-2 p-2 rounded-lg border border-gray-200 text-xs">
                          <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                            alert.severity === 'CRITICAL' ? 'text-red-600' : 'text-amber-500'
                          }`} />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 truncate">{alert.message || alert.alert_type}</div>
                            <div className="text-gray-400 mt-0.5">
                              {alert.status} · {alert.created_at ? new Date(alert.created_at).toLocaleString() : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('select_mission')}</h3>
                <p className="text-gray-600">{t('select_mission_desc')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mission Creator Modal */}
      <MissionCreator
        isOpen={showMissionCreator}
        onClose={() => {
          setShowMissionCreator(false)
          setEditingMission(null)
        }}
        onMissionCreated={handleMissionCreated}
        editingMission={editingMission}
        showMessage={showMessage}
      />

      {/* Task Manager Modal */}
      {showTaskManager && selectedMission && (
        <TaskManager
          missionId={selectedMission.id}
          missionName={selectedMission.mission_name}
          missionStaff={getMissionStaff(selectedMission)} // Staff assigned to this mission
          missionVehicles={getMissionVehicles(selectedMission)} // Vehicles assigned to this mission
          missionAlerts={getMissionAlerts(selectedMission)} // Alerts assigned to this mission
          onClose={() => setShowTaskManager(false)}
          showMessage={showMessage}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingMission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{t('delete_mission')}</h3>
                  <p className="text-sm text-gray-600">{t('action_cannot_undone')}</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to delete the mission <strong>"{deletingMission.mission_name}"</strong>?
                </p>
                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>{t('warning')}:</strong> This will permanently delete:
                  </p>
                  <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
                    <li>Mission details and progress</li>
                    <li>All associated tasks</li>
                    <li>Mission history and logs</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setDeletingMission(null)
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleDeleteMission}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{t('delete_mission')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MissionDashboard